import { Box, useMediaQuery, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import BookingCreateForm from '../../booking/components/BookingCreateForm';
import CleaningForm from '../../cleaning/component/CleaningForm';
import ErrorBox from '../../core/components/ErrorBox';
import { usePropertyId } from '../../core/hooks/usePropertyId';
import SpinnerIcon from '../../main/component/icon/SpinnerIcon';
import { baseUrl } from '../../main/client';
import PropertySelectionDialog from '../../property/components/PropertySelectionDialog';
import useProperty from '../../property/hooks/useProperty';
import useCalendarData, { type BlockedDate } from '../hooks/useCalendarData';
import type { CalendarBlockedDate, CalendarBooking, CalendarCleaning, CalendarEvent, DateSelection, MissingCleaning } from '../types/calendar';
import BlockedDateDialog from './BlockedDateDialog';
import BookingPopover from './BookingPopover';
import CalendarGrid from './CalendarGrid';
import CalendarHeader from './CalendarHeader';
import DayDetailsDrawer from './DayDetailsDrawer';

interface BlockedDateFormData {
    startDate: string;
    endDate: string;
    blockType: 'maintenance' | 'personal';
    reason: string;
}

const CalendarPage = () => {
    const propertyId = usePropertyId();
    const { property, isCleaningEnabled } = useProperty();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const pathname = window.location.hash.replace('#', '');

    const isLegacyRoute = pathname === '/calendar';
    const isPropertyRoute = /^\/\d+\/calendar/.test(pathname);

    // Month/year state for navigation
    const now = DateTime.now();
    const [currentMonth, setCurrentMonth] = useState<number>(now.month);
    const [currentYear, setCurrentYear] = useState<number>(now.year);

    // Get weekStartDay from property, default to 1 (Monday) if not available
    const weekStartDay = property?.weekStartDay ?? 1;

    const { bookings, blockedDates, cleanings, missingCleanings, loading, error, reload } = useCalendarData({
        propertyId: propertyId ?? 0,
        month: currentMonth,
        year: currentYear,
        weekStartDay,
    });

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingBlockedDate, setEditingBlockedDate] = useState<BlockedDate | null>(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    // Booking popover state
    const [selectedBooking, setSelectedBooking] = useState<CalendarBooking | null>(null);
    const [bookingAnchorEl, setBookingAnchorEl] = useState<HTMLElement | null>(null);

    // Date selection state
    const [selectedRange, setSelectedRange] = useState<DateSelection>({
        startDate: null,
        endDate: null,
    });

    // Booking form state
    const [showBookingForm, setShowBookingForm] = useState(false);

    // Cleaning form state
    const [showCleaningForm, setShowCleaningForm] = useState(false);
    const [cleaningFormSuggestedTime, setCleaningFormSuggestedTime] = useState<string | undefined>(undefined);

    // Mobile day details drawer state
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [mobileDrawerDate, setMobileDrawerDate] = useState<string | null>(null);

    if (!propertyId) {
        if (isPropertyRoute) {
            return null;
        }
        if (isLegacyRoute) {
            return <PropertySelectionDialog open={true} targetPath="/calendar" />;
        }
        return null;
    }

    const handlePreviousMonth = () => {
        if (currentMonth === 1) {
            setCurrentMonth(12);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (currentMonth === 12) {
            setCurrentMonth(1);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    const handleOpenDialog = (blockedDate?: CalendarBlockedDate) => {
        if (blockedDate) {
            // Convert CalendarBlockedDate to BlockedDate for editing
            setEditingBlockedDate({
                id: blockedDate.id,
                propertyId: propertyId,
                startDate: blockedDate.startDate,
                endDate: blockedDate.endDate,
                blockType: blockedDate.blockType,
                reason: blockedDate.reason,
                created: '',
                updated: '',
            });
        } else {
            setEditingBlockedDate(null);
        }
        setSubmitError(null);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingBlockedDate(null);
        setSubmitError(null);
        // Clear selection when dialog closes
        setSelectedRange({ startDate: null, endDate: null });
    };

    const handleSubmit = async (data: BlockedDateFormData) => {
        try {
            setSubmitLoading(true);
            setSubmitError(null);

            const url = editingBlockedDate
                ? `${baseUrl}/api/properties/${propertyId}/blocked-dates/${editingBlockedDate.id}`
                : `${baseUrl}/api/properties/${propertyId}/blocked-dates`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(data),
            });

            if (response.status === 401) {
                localStorage.removeItem('token');
                window.location.reload();
                throw new Error('Unauthorized');
            }

            if (!response.ok) {
                let errorMessage = 'Failed to save blocked date';
                try {
                    const errorData = await response.json();
                    if (errorData.error) {
                        errorMessage = errorData.error;
                    }
                } catch {
                    // If JSON parsing fails, use default error message
                }
                throw new Error(errorMessage);
            }

            // Success - close dialog and reload data
            handleCloseDialog();
            await reload();
        } catch (err) {
            setSubmitError(
                err instanceof Error ? err.message : 'Failed to save blocked date'
            );
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (confirm('Are you sure you want to delete this blocked date?')) {
            try {
                const response = await fetch(
                    `${baseUrl}/api/properties/${propertyId}/blocked-dates/${id}/delete`,
                    {
                        method: 'POST',
                        headers: {
                            authorization: `bearer ${localStorage.getItem('token')}`,
                        },
                    }
                );

                if (response.status === 401) {
                    localStorage.removeItem('token');
                    window.location.reload();
                    throw new Error('Unauthorized');
                }

                if (!response.ok) {
                    let errorMessage = 'Failed to delete blocked date';
                    try {
                        const errorData = await response.json();
                        if (errorData.error) {
                            errorMessage = errorData.error;
                        }
                    } catch {
                        // If JSON parsing fails, use default error message
                    }
                    throw new Error(errorMessage);
                }

                handleCloseDialog();
                await reload();
            } catch (err) {
                alert(
                    err instanceof Error ? err.message : 'Failed to delete blocked date'
                );
            }
        }
    };

    const handleDayClick = (date: string) => {
        // On mobile, open the day details drawer instead of date selection
        if (isMobile) {
            setMobileDrawerDate(date);
            setMobileDrawerOpen(true);
            return;
        }

        if (!selectedRange.startDate) {
            // First click - set start date
            setSelectedRange({ startDate: date, endDate: null });
        } else if (!selectedRange.endDate) {
            // Second click - set end date and open dialog
            const start = selectedRange.startDate;
            const end = date;

            // Ensure start is before end
            const [finalStart, finalEnd] = start <= end ? [start, end] : [end, start];

            setSelectedRange({ startDate: finalStart, endDate: finalEnd });

            // Open dialog with pre-filled dates
            setEditingBlockedDate(null);
            setSubmitError(null);
            setDialogOpen(true);
        } else {
            // Reset and start new selection
            setSelectedRange({ startDate: date, endDate: null });
        }
    };

    const handleBookingClick = (booking: CalendarBooking, anchorEl: HTMLElement) => {
        setSelectedBooking(booking);
        setBookingAnchorEl(anchorEl);
    };

    const handleCloseBookingPopover = () => {
        setSelectedBooking(null);
        setBookingAnchorEl(null);
    };

    const handleViewBookingDetails = (bookingId: number) => {
        // Navigate to booking details page
        window.location.hash = `#/${propertyId}/bookings/${bookingId}`;
    };

    const handleBlockedDateClick = (blockedDate: CalendarBlockedDate) => {
        handleOpenDialog(blockedDate);
    };

    const handleCleaningClick = (_cleaning: CalendarCleaning) => {
        // Navigate to cleaning page
        window.location.hash = `#/${propertyId}/cleaning`;
    };

    const handleMissingCleaningClick = (missingCleaning: MissingCleaning) => {
        // Use suggested date if available (Monday for Sunday checkouts), otherwise use checkout date
        const dateToUse = missingCleaning.suggestedDate ?? missingCleaning.checkoutDate;
        // Format as datetime-local string with default time of 12:00
        const suggestedTime = `${dateToUse}T12:00`;
        setCleaningFormSuggestedTime(suggestedTime);
        setShowCleaningForm(true);
    };

    const handleCleaningFormClose = async () => {
        setShowCleaningForm(false);
        setCleaningFormSuggestedTime(undefined);
        await reload();
    };

    // Compute all events for the mobile drawer
    const allEvents: CalendarEvent[] = useMemo(() => {
        const result: CalendarEvent[] = [];

        // Add bookings
        bookings.forEach((booking) => {
            result.push({
                id: booking.id,
                type: 'booking',
                startDate: booking.checkin,
                endDate: booking.checkout,
                label: booking.guestName,
                booking,
            });
        });

        // Add blocked dates
        blockedDates.forEach((blocked) => {
            result.push({
                id: blocked.id,
                type: blocked.blockType === 'maintenance' ? 'blocked-maintenance' : 'blocked-personal',
                startDate: blocked.startDate,
                endDate: blocked.endDate,
                label: blocked.blockType === 'maintenance' ? 'Maintenance' : blocked.reason ?? 'Personal',
                blockedDate: blocked,
            });
        });

        // Add cleanings
        const cleaningsToShow = isCleaningEnabled() ? cleanings : [];
        cleaningsToShow.forEach((cleaning) => {
            result.push({
                id: cleaning.id,
                type: cleaning.isPaid ? 'cleaning' : 'cleaning-unpaid',
                startDate: cleaning.date,
                endDate: cleaning.date,
                label: cleaning.isPaid ? `Cleaning ${cleaning.time}` : `Unpaid ${cleaning.time}`,
                cleaning,
            });
        });

        // Add missing cleanings
        const missingCleaningsToShow = isCleaningEnabled() ? missingCleanings : [];
        missingCleaningsToShow.forEach((missing, index) => {
            const displayDate = missing.suggestedDate ?? missing.checkoutDate;
            result.push({
                id: -(index + 1),
                type: 'missing-cleaning',
                startDate: displayDate,
                endDate: displayDate,
                label: 'No cleaning',
                missingCleaning: missing,
            });
        });

        return result;
    }, [bookings, blockedDates, cleanings, missingCleanings, isCleaningEnabled]);

    if (loading) return <SpinnerIcon />;
    if (error) return <ErrorBox error={error} />;

    // Pre-fill form data from selected range
    const prefilledFormData =
        selectedRange.startDate && selectedRange.endDate && !editingBlockedDate
            ? {
                  startDate: selectedRange.startDate,
                  endDate: selectedRange.endDate,
              }
            : undefined;

    return (
        <Box>
            <CalendarHeader
                month={currentMonth}
                year={currentYear}
                onPreviousMonth={handlePreviousMonth}
                onNextMonth={handleNextMonth}
                onAddBlockedDate={() => handleOpenDialog()}
                onAddBooking={() => setShowBookingForm(true)}
            />

            <CalendarGrid
                month={currentMonth}
                year={currentYear}
                weekStartDay={weekStartDay}
                bookings={bookings}
                blockedDates={blockedDates}
                cleanings={isCleaningEnabled() ? cleanings : []}
                missingCleanings={isCleaningEnabled() ? missingCleanings : []}
                selectedRange={selectedRange}
                onDayClick={handleDayClick}
                onBookingClick={handleBookingClick}
                onBlockedDateClick={handleBlockedDateClick}
                onCleaningClick={handleCleaningClick}
                onMissingCleaningClick={handleMissingCleaningClick}
            />

            <BlockedDateDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                onSubmit={handleSubmit}
                onDelete={editingBlockedDate ? () => handleDelete(editingBlockedDate.id) : undefined}
                editingBlockedDate={editingBlockedDate}
                prefilledDates={prefilledFormData}
                loading={submitLoading}
                error={submitError}
            />

            <BookingPopover
                booking={selectedBooking}
                anchorEl={bookingAnchorEl}
                onClose={handleCloseBookingPopover}
                onViewDetails={handleViewBookingDetails}
            />

            <BookingCreateForm
                open={showBookingForm}
                onClose={() => setShowBookingForm(false)}
            />

            {showCleaningForm && (
                <CleaningForm
                    onClose={handleCleaningFormClose}
                    suggestedTime={cleaningFormSuggestedTime}
                />
            )}

            {/* Mobile day details drawer */}
            <DayDetailsDrawer
                open={mobileDrawerOpen}
                selectedDate={mobileDrawerDate}
                events={allEvents}
                onClose={() => setMobileDrawerOpen(false)}
                onBookingClick={(booking) => handleViewBookingDetails(booking.id)}
                onBlockedDateClick={handleBlockedDateClick}
                onCleaningClick={handleCleaningClick}
                onMissingCleaningClick={handleMissingCleaningClick}
            />
        </Box>
    );
};

export default CalendarPage;
